import React from 'react';
import { Button } from '@roger/ui-components';
import { useExternalEmrCardStatus } from '../../../hooks/usePatientExternalEmrImport';
import {
  isMovingStatus,
  refreshStillShowing,
  type ExternalEmrPatientState,
} from '../../../services/externalEmrPatients';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ExternalEmrImportControls } from './ExternalEmrImportControls';
import {
  ADD_RECERT_EPISODE_LABEL,
  LAST_EPISODE_ENDED_LINE,
  SHELL_PAGE_ADD_EPISODE_LINE,
  SHELL_PAGE_BODY,
  SHELL_PAGE_TITLE,
  TAKEOVER_DUE_LINE,
  TAKE_OVER_LABEL,
  TAKE_OVER_READY_HELP,
  TAKE_OVER_WAIT_FOR_REFRESH,
  episodeRangeLine,
} from './externalEmrPatientCopy';

interface ExternalEmrChartPanelProps {
  patientId: string;
  external: ExternalEmrPatientState;
  /** Render gate for Import / Retry / Refresh / Cancel (`chart-import:start`). */
  canStart: boolean;
  /**
   * The imported chart's last episode has ended and Add Episode is open:
   * offer the recert episode as the next step.
   */
  recertDue: boolean;
  /**
   * The External EMR built the next period and this user can take it over
   * at Add Episode: point there.
   */
  takeoverDue?: boolean;
  onAddRecertEpisode: () => void;
  /** An import started, settled or was canceled: reload the chart. */
  onChanged: () => void;
  /** Why the agency cannot import now, shown in place of Import or Refresh. */
  importBlockedReason?: string | null;
  /** Take Over v2 offers this user the chart's latest imported period. */
  takeOverOffered?: boolean;
  onTakeOver?: () => void;
  /** First import, kept on the patient when a later refresh replaces the status. */
  importedAt?: string | null;
  /**
   * Readiness is still loading: show neither Import nor a reason, so Import
   * never flashes before a reason replaces it. Cancel stays.
   */
  importReadinessPending?: boolean;
}

/**
 * The External EMR line on a patient's chart.
 *
 * On a shell it is the whole page: "Not imported yet", what the census says,
 * the import status and the Import control. Add Episode stays hidden behind
 * the import, because a shell has no admission to add to.
 *
 * On an imported chart it is one row: when the chart came from the External
 * EMR, Refresh, then Take over when Take Over v2 offers it.
 * With nothing to offer, nothing is drawn.
 */
export const ExternalEmrChartPanel: React.FC<ExternalEmrChartPanelProps> = ({
  patientId,
  external,
  canStart,
  recertDue,
  takeoverDue = false,
  onAddRecertEpisode,
  onChanged,
  importBlockedReason = null,
  importReadinessPending = false,
  takeOverOffered = false,
  onTakeOver,
  importedAt = null,
}) => {
  const isShell = external.state === 'shell';
  const { live, status } = useExternalEmrCardStatus(patientId, external);

  if (!isShell) {
    // A refresh in flight, failed, or finished this week is worth a line even
    // without the slug. An older result has faded; the Import report keeps it.
    const hasRefreshToShow = refreshStillShowing(external.import, Date.now());
    // The bar always names the last refresh or the import, whoever views it.
    const hasProvenance = Boolean(
      live.last_refreshed_at ||
      (live.refresh && live.state === 'completed' && live.finished_at) ||
      importedAt ||
      live.imported_at
    );
    if (
      !recertDue &&
      !takeoverDue &&
      !canStart &&
      !hasRefreshToShow &&
      !hasProvenance &&
      !takeOverOffered
    )
      return null;
    // Take over waits for a refresh in flight; the dialog reads its result.
    const refreshing = isMovingStatus(status);
    return (
      <div
        className='flex flex-wrap items-center gap-x-3 gap-y-2 border-b bg-muted/30 px-4 py-2 md:px-6'
        data-testid='external-emr-imported-panel'
      >
        <ExternalEmrImportControls
          patientId={patientId}
          external={external}
          canStart={canStart}
          variant='page'
          layout='inline'
          onChanged={onChanged}
          blockedReason={importBlockedReason}
          startBlocked={importReadinessPending}
          showImportedPill={false}
          importedAt={importedAt}
        />
        {takeOverOffered && (
          // A disabled button gets no pointer events, so the span carries the title.
          <span
            className='shrink-0'
            title={
              refreshing ? TAKE_OVER_WAIT_FOR_REFRESH : TAKE_OVER_READY_HELP
            }
            data-testid='episode-take-over-wrapper'
          >
            <Button
              type='button'
              size='sm'
              disabled={refreshing}
              onClick={onTakeOver}
              data-testid='episode-take-over'
            >
              {TAKE_OVER_LABEL}
            </Button>
          </span>
        )}
        {takeOverOffered && refreshing && (
          // Touch has no hover, so phones read the reason as text.
          <span
            className='text-sm text-gray-700 md:hidden'
            data-testid='take-over-wait-line'
          >
            {TAKE_OVER_WAIT_FOR_REFRESH}
          </span>
        )}
        {takeoverDue && !recertDue && !takeOverOffered && (
          <span
            className='text-sm text-gray-700 md:ml-auto'
            data-testid='external-emr-takeover-due'
          >
            {TAKEOVER_DUE_LINE}
          </span>
        )}
        {recertDue && (
          <span
            className='flex flex-wrap items-center gap-2 text-sm text-gray-700 md:ml-auto'
            data-testid='external-emr-recert-due'
          >
            {LAST_EPISODE_ENDED_LINE}
            <Button
              size='sm'
              onClick={onAddRecertEpisode}
              data-testid='add-recert-episode'
            >
              {ADD_RECERT_EPISODE_LABEL}
            </Button>
          </span>
        )}
      </div>
    );
  }

  const census = external.census;
  return (
    <div className='px-4 pt-4 md:px-6' data-testid='external-emr-shell-panel'>
      <div className='space-y-2 rounded-md border border-dashed bg-muted/30 p-4'>
        <h2 className='text-base font-semibold'>{SHELL_PAGE_TITLE}</h2>
        <p className='text-sm text-gray-700'>{SHELL_PAGE_BODY}</p>
        <p className='text-sm text-gray-700'>
          {episodeRangeLine(
            formatDateToMMDDYYYY(census?.episode_start ?? null),
            formatDateToMMDDYYYY(census?.episode_end ?? null)
          )}
          {census?.primary_payer || census?.payer_type
            ? ` · Payer: ${census.primary_payer || census.payer_type}`
            : ''}
        </p>
        <ExternalEmrImportControls
          patientId={patientId}
          external={external}
          canStart={canStart}
          variant='page'
          onChanged={onChanged}
          blockedReason={importBlockedReason}
          startBlocked={importReadinessPending}
        />
        <p className='text-xs text-muted-foreground'>
          {SHELL_PAGE_ADD_EPISODE_LINE}
        </p>
      </div>
    </div>
  );
};
